import type { TFunction } from "i18next";

import {
  getActiveChild,
  getActiveSidebarFeatureId,
  type SidebarNavFeature,
} from "@/config/sidebar-nav";
import { isoWeek } from "@/lib/period-label";

export interface AnserRouteContext {
  /** The context line under "Anser" in the panel header. */
  label: string;
  /** `nav:anser.intro.<key>` for the panel's intro sentence. */
  introKey: string;
}

/** Intro by nav group, then by sub-page (a child's `titleKey`) where it differs. */
const INTRO_BY_FEATURE: Record<string, string> = {
  overview: "overview",
  teams: "teams",
  playbook: "goals",
  performance: "goals",
  goals: "comingSoon",
  reports: "reports",
  routines: "comingSoon",
};
const INTRO_BY_CHILD: Record<string, string> = {
  people: "people",
  checkIn: "checkIn",
  benchmarks: "comingSoon",
  data: "comingSoon",
};

/**
 * The panel's default context for a route: the active sub-page's label, or
 * the group's (Idag carries the ISO week, as in the sketch), and the intro
 * that fits it. Pages with more to say override it through
 * `useAnserPanelContext`.
 */
export function defaultAnserContext(
  pathname: string,
  features: SidebarNavFeature[],
  t: TFunction<"nav">,
  now: Date = new Date(),
): AnserRouteContext {
  const activeId = getActiveSidebarFeatureId(features, pathname);
  const feature = features.find((f) => f.id === activeId);
  if (!feature) return { label: t("anser.toggle"), introKey: "default" };
  const child = getActiveChild(feature, pathname);
  if (child && child.url !== feature.url) {
    return {
      label: t(child.titleKey),
      introKey: INTRO_BY_CHILD[child.titleKey] ?? INTRO_BY_FEATURE[feature.id] ?? "default",
    };
  }
  const label =
    feature.id === "overview"
      ? `${t(feature.titleKey)} · ${t("week", { week: isoWeek(now) })}`
      : t(feature.titleKey);
  return { label, introKey: INTRO_BY_FEATURE[feature.id] ?? "default" };
}
